import "./utils/audio-context-diagnostics.js";
import { initSentry, recordErrorInSentry } from "./utils/init-sentry.js";

initSentry();

import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { RouterProvider } from "react-router-dom";

import "./index.css";
import { ApplicationError } from "./components/ui/application-error.js";
import { FullScreenLoader } from "./components/ui/loaders/full-screen-loader.js";
import { AuthProvider } from "./features/auth/auth.provider.js";
import { isAdminConsentRoute } from "./features/auth/is-admin-consent-route.js";
import { DesktopBridgeProvider } from "./features/desktop-bridge/desktop-bridge.provider.js";
import { startOnboardingJourney } from "./features/onboarding/onboarding-tracker.js";
import { PlatformProvider } from "./features/platform-detection/platform.provider.js";
import { router } from "./routes/router.js";
import { useSessionIdStore } from "./store/use-session-id-store.js";
import { env } from "./utils/env.js";

void startOnboardingJourney();

useSessionIdStore.getState().initializeSessionId();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      recordErrorInSentry(error);
    },
  }),
});

const rootElement = document.querySelector("#root");
if (!rootElement) {
  throw new Error("Root element not found");
}
const root = ReactDOM.createRoot(rootElement);

// Check if we're in production environment
const isProduction = env.PUBLIC_ENVIRONMENT === "production";

// Mark React as mounted for global-error-handler.js
const ReactMountedMarker = () => {
  useEffect(() => {
    window.__bezonedReactMounted = true;
    return () => {
      window.__bezonedReactMounted = false;
    };
  }, []);
  return null;
};

// App content without StrictMode wrapper
const RouterShell = () => (
  <React.Suspense
    fallback={
      <FluentProvider theme={teamsLightTheme}>
        <FullScreenLoader message="Accessing office" />
      </FluentProvider>
    }
  >
    <FluentProvider theme={teamsLightTheme}>
      <RouterProvider router={router} />
    </FluentProvider>
  </React.Suspense>
);

const FullAppShell = () => (
  <DesktopBridgeProvider>
    <PlatformProvider>
      <AuthProvider>
        <RouterShell />
      </AuthProvider>
    </PlatformProvider>
  </DesktopBridgeProvider>
);

const AdminConsentShell = () => <RouterShell />;

const shouldUseAdminConsentShell =
  typeof window !== "undefined" &&
  isAdminConsentRoute(window.location.pathname);

const handleRootError = (error: unknown, info: React.ErrorInfo) => {
  recordErrorInSentry(error, {
    data: { componentStack: info.componentStack },
  });
};

const AppContent = () => (
  <ErrorBoundary FallbackComponent={ApplicationError} onError={handleRootError}>
    <ReactMountedMarker />
    <NuqsAdapter>
      <QueryClientProvider client={queryClient}>
        {shouldUseAdminConsentShell ? <AdminConsentShell /> : <FullAppShell />}
      </QueryClientProvider>
    </NuqsAdapter>
  </ErrorBoundary>
);

// Initial render - disable StrictMode in production
if (isProduction) {
  root.render(<AppContent />);
} else {
  root.render(
    <React.StrictMode>
      <AppContent />
    </React.StrictMode>
  );
}
